import group from "./undo_group";
import {save} from "./save";
import {eachRange} from "../helpers/column_names";
import {defaultStyles} from "../toolbar/defaults";
import {find} from "../helpers/array";
import {borderSizeMap, borderTemplates, oppositeBordersMap} from "../helpers/borders";

//do not change order, it will break server side compatibility
//add new options to the end of this list
export const style_names = [
	"color","background","text-align",
	"font-family","font-size",
	"font-style","underline","font-weight",
	"vertical-align", "wrap", "borders", "format",
	"border-right", "border-bottom","border-left", "border-top",
	"strike", "indent"
];

const _style_map = {
	format: () => "",
	"text-align": {
		left: () => "justify-content:flex-start;",
		center: () => "justify-content:center;text-align:center;",
		right: () => "justify-content:flex-end;text-align:right;"
	},
	"vertical-align": {
		top: () => "align-items:flex-start;",
		middle: () => "align-items:center;",
		bottom: () => "align-items:flex-end;"
	},
	wrap: {
		wrap: () => {
			return "white-space: normal !important;";
		}
	},
	"border-left": (obj, zoom) => {
		return getBorderCss("border-left", obj, zoom);
	},
	"border-top": (obj, zoom) => {
		return getBorderCss("border-top", obj, zoom);
	},
	"border-right": (obj, zoom) => {
		return getBorderCss("border-right", obj, zoom);
	},
	"border-bottom": (obj, zoom) => {
		return getBorderCss("border-bottom", obj, zoom);
	},
	"font-weight": obj => {
		//Roboto bold - font-weight:500 (looks better, than 700). Roboto is default font for material skins.
		if (obj["font-weight"] == "bold") {
			const family = obj["font-family"] || defaultStyles["font-family"];
			return "font-weight:" + (family == "'Roboto', sans-serif" ? 500 : 700) + ";";
		}
		return "";
	},
	underline: obj => {
		const underline = obj.underline == "underline" ? "underline" : "";
		if (underline) {
			const strike = obj.strike == "strike" ? "line-through" : "";
			return `text-decoration: ${underline} ${strike};`;
		}
		return "";
	},
	strike: obj => {
		if (obj.underline != "underline" && obj.strike == "strike")
			return "text-decoration: line-through;";
		return "";
	},
	indent: (obj, zoom) => {
		const count = obj.indent * 1;
		if (count > 0){
			const pos = obj["text-align"] == "right" ? "right" : "left";
			return { innerCss: `margin-${pos}:${(count * 8 + 12)*zoom}px;` }; // 12 - default cell margin
		}
	},
	"font-size": (obj, zoom) => {
		let fontSize = obj["font-size"];

		if (zoom != 1)
			fontSize = obj["font-size"].replace("px","") * zoom + "px";

		return "font-size:"+fontSize+";";
	},
};

export function getBorderCss(pos, obj, zoom){
	const border = obj[pos];
	if (!border) return null;

	const [color, type = "thin"] = border.split(",");
	const angle = pos === "border-left" || pos === "border-right" ? 0 : 90;

	return {
		css: borderTemplates[type](pos, color, zoom, angle),
		size: borderSizeMap[type] * zoom + "px"
	};
}

const border_checks = {
	"border-left": (cell, area, mode) => cell.column == area.start.column || mode == "no",
	"border-top": (cell, area , mode) => cell.row == area.start.row || mode == "no",
	"border-right": (cell, area, mode, span) => {
		if(span && mode == "all")
			return cell.column == span[1]*1 + span[2] - 1;
		return cell.column == area.end.column || mode == "all" || mode == "no";
	},
	"border-bottom": (cell, area, mode, span) => {
		if(span && mode == "all")
			return cell.row == span[0]*1 + span[3] - 1;
		return cell.row == area.end.row || mode == "all"|| mode == "no";
	}
};

const _style_handlers = {
	"borders": function(view, style, value, cell, area){
		area = area || view.$$("cells").getSelectArea();
		const [type, color, lineStyle] = value.split(",");

		let modes = ["border-left", "border-right", "border-bottom", "border-top"];

		if(type == "top-bottom"){
			modes = ["border-top","border-bottom"];
		}
		else if (type != "no" && type != "all" && type != "outer")
			modes = ["border-"+type];

		for (let i = 0; i < modes.length; i++){
			const mode = modes[i];
			const span = view.getSpan(cell.row, cell.column);
			if (border_checks[mode](cell, area, type, span)){
				const border = type == "no" ? "" : `${color},${lineStyle}`;
				style = _updateStyle(view, style, mode, border, cell);
			}
		}
		return style;
	}
};

const toggleableStyles = {
	"font-weight": ["normal", "bold"],
	"font-style": ["normal", "italic"],
	"underline": ["normal", "underline"],
	"strike": ["normal", "strike"],
	"wrap": ["nowrap", "wrap"],
};

var sizeEl;
export function init(view){
	view.attachEvent("onStyleSet", (name, value) => _applyStyles(view, name, value));
	view.attachEvent("onDataParse", (data) => _parse(view, data));
	view.attachEvent("onDataSerialize", (data) => _serialize(view, data));
	view.attachEvent("onReset", () => reset(view));
	view.attachEvent("onUndo", (type, row, column, style) => {
		if(type == "style")
			_undoStyle(view, row, column, style);
	});

	reset(view);

	if (!sizeEl)
		sizeEl = webix.html.create("DIV", { style:"visibility:hidden; position:absolute; top:0px; left:0px; height:auto;"}, "");
}

function reset(view){
	view._styles = {};
	view._styles_cache = {};
	view._styles_max = 1;

	let prefix = ".wss_"+view.$index;
	webix.html.removeStyle(prefix);
}

export function zoomStyles(view){
	const data = {};
	_serialize(view, data);
	reset(view);
	_parse(view, data, true);
}

export function getStyle(view, cell, page){
	if(page && page != view.getActiveSheet()){
		const sheet = view._mData.getPage(page);
		if(sheet){
			const obj = sheet.getCell(cell.row-1, cell.column-1);
			if (obj && obj.style){
				const styles = find(view._sheets, sheet => sheet.name == page).content.styles || [];
				const style = find(styles, stl => stl[0] == obj.style);
				if(style)
					return {
						id: obj.style,
						text: style[1],
						props: styleFromText(style[1])
					};
			}
		}
	}
	else{
		const obj = view._mPage.getCell(cell.row-1, cell.column-1);
		if (obj && obj.style)
			return view._styles[obj.style];
	}

	return null;
}

export function getFormat(view, row, column){
	var obj = view._mPage.getCell(row-1, column-1);
	return (obj && obj.format) || "";
}

// undo
function _undoStyle(view, row, column, style){
	var cell = {row: row, column: column};
	setStyle(view, cell, style);
}

function _serialize(view, obj){
	var styles = [];

	for (var key in view._styles_cache)
		styles.push([view._styles_cache[key].id, key]);

	obj.styles = styles;
}

export function addStyle(view, props, origin, page){
	const style = {
		props: styleFromText( style_names.map(()=>"").join(";") )
	};

	if (origin)
		for (let key in origin.props)
			style.props[key] = origin.props[key];

	for (let  key in props)
		style.props[key] = props[key];

	style.text = styleToText(style);

	let sheet;
	if(page && page != view.getActiveSheet()){
		sheet = find(view._sheets, sheet => sheet.name == page);
		if(!sheet)
			return null;
		if(sheet.content.styles){
			const sameStyle = find(sheet.content.styles, stl => stl[1] == style.text);
			if(sameStyle)
				return webix.extend(style, {id: sameStyle[0]});
		}
		else
			sheet.content.styles = [];
	}
	else{
		const cache = view._styles_cache[style.text];
		if (cache)
			return cache;
	}

	_addStyle(view, style, false, sheet);

	return style;
}

function _parse(view, obj, zoom){
	if (obj.styles)
		for (let i=0; i < obj.styles.length; i++){
			var styleObj = obj.styles[i];
			var style = {
				id: styleObj[0],
				text:styleObj[1],
				props: styleFromText(styleObj[1])
			};

			_addStyle(view, style, true);
		}

	if(!zoom)
		for (let i = 0; i < obj.data.length; i++){
			var [row, column, , css] = obj.data[i];

			if (css)
				update_style_data(view, row, column, view._styles[css]);
		}
}

function _applyStyles(view, name, value){
	if(!view.getSelectedId())
		return;
	group.set(function(){
		const areas = view._table.getAllSelectAreas();
		for(let areaName in areas){
			const area = areas[areaName];
			for(let row = area.start.row; row <= area.end.row; row++)
				for(let column = area.start.column; column <= area.end.column; column++)
					_applyCellStyles(view, {row, column}, name, value, area);
		}
	});
	view.callEvent("onCommand", [{id:"toolbar-update"}]);
	view.refresh();
}

function _applyCellStyles(view, cell, name, value, area){
	const ostyle = getStyle(view, cell);

	if(name == "indent"){
		const oldVal = ((ostyle && ostyle.props[name]) || 0) * 1;

		if(!oldVal && value == -1)
			return;

		value += oldVal;
	}

	const nstyle = _updateStyle(view, ostyle, name, value, cell, area);

	if (nstyle && nstyle != ostyle)
		_setStyle(view, cell, nstyle, null, false);
}

function _updateStyle(view, style, name, value, cell, area){
	if (_style_handlers[name]) {
		return _style_handlers[name](view, style, value, cell, area);
	}
	const toggledStyle = value === undefined && toggleableStyles[name]; 

	if (style && style.props[name] == value && !toggledStyle) return style;

	const nstyle = {
		text: "", 
		id: 0, 
		props: (style ? webix.copy(style.props) : {}) 
	};

	if (toggledStyle) {
		const old = nstyle.props[name];
		const i = !old ? 0 : toggledStyle.findIndex(s => s == old);
		value = toggledStyle[i^1];
	}

	nstyle.props[name] = value;
	nstyle.text = styleToText(nstyle);

	const cache = view._styles_cache[nstyle.text];
	if (cache)
		return cache;

	_addStyle(view, nstyle);

	return nstyle;
}

function update_style_data(view, row, column, style, page){
	const isActiveSheet = !page || page == view.getActiveSheet();
	const sheet = isActiveSheet ? view._mPage : view._mData.getPage(page);
	if(sheet){
		const obj = sheet.getCell(row-1, column-1, true);
		obj.style = style && style.id;
		obj.format = style && style.props.format;
		if(!isActiveSheet){
			const data = find(view._sheets, sheet => sheet.name == page).content.data;
			const item = find(data, item => item[0] == row && item[1] == column);
			if(item) item[3] = obj.style;
			else data.push([row, column, "", obj.style]);
		}
	}
}

export function setStyle(view, cell, style, page){
	return _setStyle(view, cell, style, page);
}

export function setRangeStyle(view, range, style, page) {
	range.split(";").forEach(ref => {
		eachRange(ref, view, (view, cell, data) => _setStyle(view, cell, data.style, data.page, false), {style, page});
	});

	if(!page || page == view.getActiveSheet())
		view.callEvent("onCommand", [{id:"toolbar-update"}]);
}

export function getTextSize(view, text, css, baseWidth){
	sizeEl.innerHTML = text;
	sizeEl.style.width = baseWidth ? baseWidth+"px" : "auto";

	sizeEl.className = "webix_table_cell webix_cell "+css;
	view._table.$view.appendChild(sizeEl);

	const width = Math.max(0, sizeEl.offsetWidth+1);
	const height = Math.max(0, sizeEl.offsetHeight+1);

	view._table.$view.removeChild(sizeEl);
	sizeEl.innerHTML = "";

	return { width, height };
}

function _setStyle(view, cell, style, page, updateToolbar){
	const row = cell.row;
	const column = cell.column;
	const old = getStyle(view, cell, page);
	const activeSheet = view.getActiveSheet();
	if(!page)
		page = activeSheet;
	if(view.isCellLocked(row, column, page)) return false;

	if(view.callEvent("onBeforeStyleChange", [row, column, style, old, page])){
		group.set(()=>{
			const format = style && style.props.format || null;
			const oldFormat = old && old.props.format || null;
			const formatChanged = format != oldFormat;

			// update borders
			if(!view.$updatingBorders && view.config.borderCollapse && style){
				const updatedStyle = updateStyleBorders(view, row, column, style, page);
				if(updatedStyle)
					return view.setStyle(row, column, updatedStyle, page);
			}

			if(!formatChanged || (formatChanged && view.callEvent("onBeforeFormatChange", [cell.row, cell.column, format, oldFormat, page]))){
				update_style_data(view, cell.row, cell.column, style, page);
				view.callEvent("onStyleChange", [cell.row, cell.column, style, old, page]);

				if(formatChanged)
					view.callEvent("onFormatChange", [cell.row, cell.column, format, oldFormat, page ]);

				view.saveCell(cell.row,cell.column,page);

				if(updateToolbar !== false && page == activeSheet)
					view.callEvent("onCommand", [{id:"toolbar-update"}]);
			}
		});
	}
}

export function updateStyleBorders(view, row, column, style, page){
	view.$updatingBorders = true;
	const props = style.props;
	const span = view.getSpan(row, column, page);
	let updatedStyle = webix.copy(style);

	if(props["border-top"]){
		let topCellStyle = view.getStyle(row-1, column, page);
		if(topCellStyle && topCellStyle.props["border-bottom"]){
			const topSpan = view.getSpan(row-1, column, page);
			const topLocked = view.isCellLocked(row-1, column, page);
			if(!topLocked){
				topCellStyle = view.addStyle({"border-bottom": span || topSpan ? "" : props["border-top"]}, topCellStyle, page);
				view.setStyle(row-1, column, topCellStyle, page);
			}
			if(topLocked || (!span && !topSpan))
				updatedStyle = view.addStyle({"border-top": ""}, updatedStyle, page);
		}
	}

	if(props["border-right"]){
		let rightCellStyle = view.getStyle(row, column+1, page);
		if(rightCellStyle && rightCellStyle.props["border-left"]){
			if(view.isCellLocked(row, column+1, page))
				updatedStyle = view.addStyle({"border-right": ""}, updatedStyle, page);
			else{
				rightCellStyle = view.addStyle({"border-left": ""}, rightCellStyle, page);
				view.setStyle(row, column+1, rightCellStyle, page);
			}
		}
	}

	if(props["border-bottom"]){
		let bottomCellStyle = view.getStyle(row+1, column, page);
		if(bottomCellStyle && bottomCellStyle.props["border-top"]){
			if(view.isCellLocked(row+1, column, page))
				updatedStyle = view.addStyle({"border-bottom": ""}, updatedStyle, page);
			else{
				bottomCellStyle = view.addStyle({"border-top": ""}, bottomCellStyle, page);
				view.setStyle(row+1, column, bottomCellStyle, page);
			}
		}
	}

	if(props["border-left"]){
		let leftCellStyle = view.getStyle(row, column-1, page);
		if(leftCellStyle && leftCellStyle.props["border-right"]){
			const leftSpan = view.getSpan(row, column-1, page);
			const leftLocked = view.isCellLocked(row, column-1, page);
			if(!leftLocked){
				leftCellStyle = view.addStyle({"border-right": span || leftSpan ? "" : props["border-left"]}, leftCellStyle, page);
				view.setStyle(row, column-1, leftCellStyle, page);
			}
			if(leftLocked || (!span && !leftSpan))
				updatedStyle = view.addStyle({"border-left": ""}, updatedStyle, page);
		}
	}

	delete view.$updatingBorders;
	if(updatedStyle.id !== style.id)
		return updatedStyle;
}

function _buildCssString(view, style) {
	let css = "";
	let innerCss = "";
	let borders = {};

	for (var key in style){
		if (style[key]){
			if( _style_map[key]){
				if(_style_map[key][style[key]])
					css += _style_map[key][style[key]](style);
				else if(typeof  _style_map[key] == "function"){
					const outCss = _style_map[key](style, view._zoom);

					if(key.indexOf("border") == 0)
						borders[key] = outCss;
					else if(typeof outCss == "object"){
						css += outCss.css || "";
						innerCss += outCss.innerCss || "";
					}
					else
						css += outCss;
				}
			}
			else
				css+= key+":"+style[key]+";";
		}
	}

	return {css, innerCss, borders};
}

function _addStyle(view, style, silent, sheet){
	if(sheet && sheet.name != view.getActiveSheet()){
		let index = 1;
		while(!style.id || find(sheet.content.styles, stl => stl[0] == style.id))
			style.id = "wss"+(index++);
		sheet.content.styles.push([style.id, style.text]);
	}
	else {
		view._styles_cache[style.text] = style;

		while (!style.id || view._styles[style.id])
			style.id = "wss"+(view._styles_max++);
		view._styles[style.id] = style;

		const {css, innerCss, borders} = _buildCssString(view, style.props);
		const prefix = ".wss_"+view.$index;

		Object.entries(oppositeBordersMap).forEach(([border, opposites]) => {
			const currentBorder = borders[border];
			if (!currentBorder) return;

			const extraCss = opposites
				.map(opposite => {
					const oppositeBorder = borders[opposite];
					return oppositeBorder ? `${opposite}: ${oppositeBorder.size} solid transparent;` : "";
				})
				.join("");

			webix.html.addStyle(
				`${prefix} .${style.id} .webix_ssheet_${border.replace("-","_")}{${extraCss}${currentBorder.css}}`,
				prefix
			);
		});

		webix.html.addStyle(prefix+" ."+style.id+"{"+css+"}", prefix);
		webix.html.addStyle(prefix+" ."+style.id+" div:first-child{"+innerCss+"}", prefix);
	}

	if (!silent)
		save(view, "styles", {
			name: style.id,
			text: style.text,
			page: sheet ? sheet.name : view.getActiveSheet()
		});
}

export function styleToText(style){
	var id = [];
	for (var i=0; i<style_names.length; i++)
		id.push(style.props[style_names[i]]);
	return id.join(";");
}

export function styleFromText(text){
	var parts = text.split(";");
	var props = {};
	for (var i=0; i<style_names.length; i++)
		props[style_names[i]] = parts[i];
	return props;
}

export function clearRangeStyle(view, range, page){
	group.set(function(){
		range.split(";").forEach(ref => {
			eachRange(ref, view, function(view, cell){
				let style = getStyle(view, cell, page);
				if (style)
					_setStyle(view, cell, null, page, false);
			});
		});
		view.callEvent("onCommand", [{id:"toolbar-update"}]);
	});
}

export function compactStyles(view){
	const serialized = view.serialize();

	serialized.styles = removeUnusedStyles(serialized);

	reset(view);
	_parse(view, serialized);
}

export function removeUnusedStyles(serialized, usedFormats){
	const data = serialized.data;
	const styles = serialized.styles;
	const fmtIndex = style_names.indexOf("format");

	const used = {};

	for (let i = 0; i < data.length; i++){
		const name = data[i][3];
		if (name)
			used[name] = 1;
	}

	const newStyles = [];
	for(let i = 0; i < styles.length; i++){
		const name = styles[i][0];
		if (used[name]){
			newStyles.push(styles[i]);
			if(usedFormats){
				const format = styles[i][1].split(";")[fmtIndex];
				if(format)
					usedFormats[format] = 1;
			}
		}
	}

	return newStyles;
}

export function setSpanInnerStyles(view, cell, x, y, page){
	const row = cell.row*1;
	const column = cell.column*1;

	let firstCellStyles = view.getStyle(row, column, page);
	firstCellStyles = firstCellStyles ? firstCellStyles.props : {};

	const baseStyle = webix.copy(firstCellStyles);
	["border-left", "border-top", "border-right", "border-bottom"].forEach(pos => delete baseStyle[pos]);

	for(let r = row; r < row+y; r++){
		for(let c = column; c < column+x; c++) {
			let currentCellStyle = view.getStyle(r, c, page);
			currentCellStyle = currentCellStyle ? currentCellStyle.props : {};

			const cellStyle = webix.copy(baseStyle);
			if(r == row)
				cellStyle["border-top"] = currentCellStyle["border-top"]; 
			if(r == row+y-1)
				cellStyle["border-bottom"] = currentCellStyle["border-bottom"];
			if(c == column)
				cellStyle["border-left"] = currentCellStyle["border-left"];
			if(c == column+x-1)
				cellStyle["border-right"] = currentCellStyle["border-right"];

			const style = Object.keys(cellStyle).length ? view.addStyle(cellStyle, null, page) : null;
			view.setStyle(r, c, style, page);
		}
	}
}